/**
 * DashboardViewToggle - Toggle élégant entre vue Cards et Charts
 * Inspiré QuickBooks/Sage
 */
import { LayoutGrid, BarChart3, Sparkles } from 'lucide-react';

export type DashboardViewMode = 'cards' | 'charts';

interface DashboardViewToggleProps {
  activeView: DashboardViewMode;
  onViewChange: (view: DashboardViewMode) => void;
  className?: string;
}

export function DashboardViewToggle({
  activeView,
  onViewChange,
  className = ''
}: DashboardViewToggleProps) {
  return (
    <div className={`inline-flex items-center bg-gray-100 dark:bg-gray-800 rounded-xl p-1 ${className}`}>
      <button
        onClick={() => onViewChange('cards')}
        className={`
          relative flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
          transition-all duration-200 ease-out
          ${activeView === 'cards'
            ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }
        `}
        aria-pressed={activeView === 'cards'}
      >
        <LayoutGrid className="h-4 w-4" />
        <span>Widgets</span>
        {activeView === 'cards' && (
          <span className="absolute -top-1 -right-1 flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
          </span>
        )}
      </button>
      
      <button
        onClick={() => onViewChange('charts')}
        className={`
          relative flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
          transition-all duration-200 ease-out
          ${activeView === 'charts'
            ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }
        `}
        aria-pressed={activeView === 'charts'}
      >
        <BarChart3 className="h-4 w-4" />
        <span>Graphiques</span>
        {activeView === 'charts' && (
          <span className="absolute -top-1 -right-1 flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
          </span>
        )}
      </button>
    </div>
  );
}

// Version compacte pour mobile
export function DashboardViewToggleCompact({
  activeView,
  onViewChange,
  className = ''
}: DashboardViewToggleProps) {
  return (
    <div className={`inline-flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5 ${className}`}>
      <button
        onClick={() => onViewChange('cards')}
        className={`
          p-2 rounded-md transition-all duration-200
          ${activeView === 'cards'
            ? 'bg-white dark:bg-gray-700 text-primary shadow-sm'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }
        `}
        aria-label="Vue widgets"
        title="Vue widgets"
      >
        <LayoutGrid className="h-5 w-5" />
      </button>
      
      <button
        onClick={() => onViewChange('charts')}
        className={`
          p-2 rounded-md transition-all duration-200
          ${activeView === 'charts'
            ? 'bg-white dark:bg-gray-700 text-primary shadow-sm'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }
        `}
        aria-label="Vue graphiques"
        title="Vue graphiques"
      >
        <BarChart3 className="h-5 w-5" />
      </button>
    </div>
  );
}
